import { DateTime, DurationUnit } from 'luxon';

import { ComputedPeriodDurationDto } from '../modules/shared/dtos/computed-period-duration.dto';

import { toDuration } from './to-duration.function';

/**
 * Calculates the duration between two dates in specified units. If no units are provided, it defaults to days.
 * Similar to {@link toDuration}, but returns a {@link ComputedPeriodDurationDto} instead of a {@link Duration}
 * and always ceils the duration components to the next whole number.
 * @param dateFrom - The start date.
 * @param dateTo - The end date.
 * @param options - The options to calculate the duration.
 * @returns The duration between the two dates in the specified units. If the duration is not long enough
 * to be represented in the specified units, they will be omitted.
 * @example
 * toComputedPeriodDuration('2021-01-01', '2021-01-31', { units: ['days'] }); // Duration { days: 30 }
 * toComputedPeriodDuration('2021-01-01', '2021-01-31', { units: ['months'] }); // Duration { months: 1 }
 * toComputedPeriodDuration('2021-01-01', '2021-01-31', { units: ['days', 'months'] }); // Duration { days: 30, months: 1 }
 * toComputedPeriodDuration('2021-01-01', '2021-01-31', { units: ['days', 'months', 'years'] }); // Duration { days: 30, months: 1, years: 0 }
 * toComputedPeriodDuration('2021-01-01', '2021-01-31', { units: ['days', 'months', 'years', 'hours'] }); // Duration { days: 30, months: 1, years: 0, hours: 0 }
 */
export function toComputedPeriodDuration(
	dateFrom: Date | DateTime | string | null | undefined,
	dateTo: Date | DateTime | string | null | undefined,
	options?: {
		units?: DurationUnit[] | undefined;
	},
): ComputedPeriodDurationDto | null {
	const duration = toDuration(dateFrom, dateTo, options);

	return {
		years: Math.ceil(duration?.years ?? 0),
		quarters: Math.ceil(duration?.quarters ?? 0),
		months: Math.ceil(duration?.months ?? 0),
		weeks: Math.ceil(duration?.weeks ?? 0),
		days: Math.ceil(duration?.days ?? 0),
		hours: Math.ceil(duration?.hours ?? 0),
		minutes: Math.ceil(duration?.minutes ?? 0),
		seconds: Math.ceil(duration?.seconds ?? 0),
		milliseconds: Math.ceil(duration?.milliseconds ?? 0),
	};
}
